import { Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { AuthGuard } from '@/components/auth/AuthGuard';
import { ModalFeedbackProvider } from '@/components/feedback/modalSlot';
import { VisualFeedbackWidget } from '@/components/feedback/VisualFeedbackWidget';
import { AppLayout } from '@/components/layout/AppLayout';
import { DocumentTitle } from '@/components/layout/DocumentTitle';
import { PageChromeProvider } from '@/components/layout/pageChrome';
import { OnboardingOperationTracker } from '@/components/onboarding/OnboardingOperationTracker';
import { getWelcome } from '@/components/onboarding/welcomeFlow';
import { ReportingErrorBoundary } from '@/components/ui/ReportingErrorBoundary';
import { EnvironmentProvider } from '@/contexts/EnvironmentContext';
import { KnowledgeOperationProgressProvider } from '@/contexts/KnowledgeOperationProgressContext';
import { PinnedPagesProvider } from '@/contexts/PinnedPagesContext';
import { RetrainProgressProvider } from '@/contexts/RetrainProgressContext';
import { WebsiteCrawlProgressProvider } from '@/contexts/WebsiteCrawlProgressContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

const AccountsPage = lazyWithRetry(() =>
    import('@/pages/AccountsPage').then((m) => ({ default: m.AccountsPage }))
);
const AnalyticsPage = lazyWithRetry(() =>
    import('@/pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage }))
);
const InsightsPage = lazyWithRetry(() =>
    import('@/pages/InsightsPage').then((m) => ({ default: m.InsightsPage }))
);
const TopTopicsPage = lazyWithRetry(() =>
    import('@/pages/TopTopicsPage').then((m) => ({ default: m.TopTopicsPage }))
);
const AISectionsPage = lazyWithRetry(() =>
    import('@/pages/AISectionsPage').then((m) => ({ default: m.AISectionsPage }))
);
const CuratedContentPage = lazyWithRetry(() =>
    import('@/pages/CuratedContentPage').then((m) => ({ default: m.CuratedContentPage }))
);
const AuthCallbackPage = lazyWithRetry(() =>
    import('@/pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage }))
);
const AgentClaimPage = lazyWithRetry(() =>
    import('@/pages/AgentClaimPage').then((m) => ({ default: m.AgentClaimPage }))
);
const OAuthConsentPage = lazyWithRetry(() =>
    import('@/pages/OAuthConsentPage').then((m) => ({ default: m.OAuthConsentPage }))
);
const ConversationsPage = lazyWithRetry(() =>
    import('@/pages/ConversationsPage').then((m) => ({ default: m.ConversationsPage }))
);
const DocumentationPage = lazyWithRetry(() =>
    import('@/pages/DocumentationPage').then((m) => ({ default: m.DocumentationPage }))
);
const DynamicQuestionsPage = lazyWithRetry(() =>
    import('@/pages/DynamicQuestionsPage').then((m) => ({ default: m.DynamicQuestionsPage }))
);
const HomePage = lazyWithRetry(() =>
    import('@/pages/HomePage').then((m) => ({ default: m.HomePage }))
);
const ClientHealthPage = lazyWithRetry(() =>
    import('@/pages/ClientHealthPage').then((m) => ({ default: m.ClientHealthPage }))
);
const RoseForAgentsPage = lazyWithRetry(() =>
    import('@/pages/RoseForAgentsPage').then((m) => ({ default: m.RoseForAgentsPage }))
);
const RoseForAgentsAccessPage = lazyWithRetry(() =>
    import('@/pages/RoseForAgentsAccessPage').then((m) => ({ default: m.RoseForAgentsAccessPage }))
);
const RoseForAgentsChecksPage = lazyWithRetry(() =>
    import('@/pages/RoseForAgentsChecksPage').then((m) => ({ default: m.RoseForAgentsChecksPage }))
);
const RoseForAgentsProfilePage = lazyWithRetry(() =>
    import('@/pages/RoseForAgentsProfilePage').then((m) => ({
        default: m.RoseForAgentsProfilePage,
    }))
);
const IntegrationsPage = lazyWithRetry(() =>
    import('@/pages/integrations/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage }))
);
const VisitorsPage = lazyWithRetry(() =>
    import('@/pages/VisitorsPage').then((m) => ({ default: m.VisitorsPage }))
);
const SettingsPage = lazyWithRetry(() =>
    import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const PlanUsagePage = lazyWithRetry(() =>
    import('@/pages/PlanUsagePage').then((m) => ({ default: m.PlanUsagePage }))
);
const ComplianceSettingsPage = lazyWithRetry(() =>
    import('@/pages/ComplianceSettingsPage').then((m) => ({
        default: m.ComplianceSettingsPage,
    }))
);
const ClientExperimentPage = lazyWithRetry(() =>
    import('@/pages/ClientExperimentPage').then((m) => ({ default: m.ClientExperimentPage }))
);
const StaffExperimentsPage = lazyWithRetry(() =>
    import('@/pages/StaffExperimentsPage').then((m) => ({ default: m.StaffExperimentsPage }))
);
const PersonIdentityDiagnosticsPage = lazyWithRetry(() =>
    import('@/pages/PersonIdentityDiagnosticsPage').then((m) => ({
        default: m.PersonIdentityDiagnosticsPage,
    }))
);
const StaffExperimentDetailPage = lazyWithRetry(() =>
    import('@/pages/StaffExperimentDetailPage').then((m) => ({
        default: m.StaffExperimentDetailPage,
    }))
);
const StaffExperimentNewPage = lazyWithRetry(() =>
    import('@/pages/StaffExperimentNewPage').then((m) => ({
        default: m.StaffExperimentNewPage,
    }))
);
const StaffAnnotationQueuePage = lazyWithRetry(() =>
    import('@/pages/StaffAnnotationQueuePage').then((m) => ({
        default: m.StaffAnnotationQueuePage,
    }))
);
const StaffNoticesPage = lazyWithRetry(() =>
    import('@/pages/StaffNoticesPage').then((m) => ({
        default: m.StaffNoticesPage,
    }))
);
const WebmcpUsagePage = lazyWithRetry(() =>
    import('@/pages/WebmcpUsagePage').then((m) => ({
        default: m.WebmcpUsagePage,
    }))
);
const KnowledgeSection = lazyWithRetry(() =>
    import('@/pages/KnowledgeSection').then((m) => ({ default: m.KnowledgeSection }))
);
const GuidelinesSection = lazyWithRetry(() =>
    import('@/pages/GuidelinesSection').then((m) => ({ default: m.GuidelinesSection }))
);
const WebsiteAgentPage = lazyWithRetry(() =>
    import('@/pages/WebsiteAgentPage').then((m) => ({ default: m.WebsiteAgentPage }))
);
const WebsiteAgentSettingPage = lazyWithRetry(() =>
    import('@/pages/WebsiteAgentSettingPage').then((m) => ({ default: m.WebsiteAgentSettingPage }))
);
const ContentAgentSettingsPage = lazyWithRetry(() =>
    import('@/pages/ContentAgentSettingsPage').then((m) => ({
        default: m.ContentAgentSettingsPage,
    }))
);
const GeoQuestionsPage = lazyWithRetry(() =>
    import('@/pages/GeoQuestionsPage').then((m) => ({ default: m.GeoQuestionsPage }))
);
const GeoFaqsPage = lazyWithRetry(() =>
    import('@/pages/GeoFaqsPage').then((m) => ({ default: m.GeoFaqsPage }))
);
const GeoDomainPage = lazyWithRetry(() =>
    import('@/pages/GeoDomainPage').then((m) => ({ default: m.GeoDomainPage }))
);
const AgentPlaceholderPage = lazyWithRetry(() =>
    import('@/pages/AgentPlaceholderPage').then((m) => ({ default: m.AgentPlaceholderPage }))
);
const ClientsPage = lazyWithRetry(() =>
    import('@/pages/ClientsPage').then((m) => ({ default: m.ClientsPage }))
);
const ClientEditorPage = lazyWithRetry(() =>
    import('@/pages/ClientEditorPage').then((m) => ({ default: m.ClientEditorPage }))
);
const AdminSitesPage = lazyWithRetry(() =>
    import('@/pages/AdminSitesPage').then((m) => ({ default: m.AdminSitesPage }))
);
const SupportPage = lazyWithRetry(() =>
    import('@/pages/SupportPage').then((m) => ({ default: m.SupportPage }))
);
const UsersPage = lazyWithRetry(() =>
    import('@/pages/UsersPage').then((m) => ({ default: m.UsersPage }))
);
const ContentAgentPage = lazyWithRetry(() =>
    import('@/pages/ContentAgentPage').then((m) => ({ default: m.ContentAgentPage }))
);
const DynamicQuestionsLabPage = lazyWithRetry(() =>
    import('@/pages/DynamicQuestionsLabPage').then((m) => ({ default: m.DynamicQuestionsLabPage }))
);
const PublishPage = lazyWithRetry(() =>
    import('@/components/setup/PublishPage').then((m) => ({ default: m.PublishPage }))
);
const DesignSystemPage = lazyWithRetry(() =>
    import('@/pages/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage }))
);
const PlaygroundPage = lazyWithRetry(() =>
    import('@/pages/PlaygroundPage').then((m) => ({ default: m.PlaygroundPage }))
);
const PlaygroundDatasetsPage = lazyWithRetry(() =>
    import('@/pages/PlaygroundDatasetsPage').then((m) => ({ default: m.PlaygroundDatasetsPage }))
);
// Enhanced onboarding-step config screens (IX-3950)
const BrandTonePage = lazyWithRetry(() =>
    import('@/pages/setup/BrandTonePage').then((m) => ({ default: m.BrandTonePage }))
);
const SuggestedQuestionsPage = lazyWithRetry(() =>
    import('@/pages/setup/SuggestedQuestionsPage').then((m) => ({
        default: m.SuggestedQuestionsPage,
    }))
);
const CtaPage = lazyWithRetry(() =>
    import('@/pages/setup/CtaPage').then((m) => ({ default: m.CtaPage }))
);
const WelcomeLoader = lazyWithRetry(() =>
    import('@/components/onboarding/WelcomeLoader').then((m) => ({ default: m.WelcomeLoader }))
);
const PlansScreen = lazyWithRetry(() =>
    import('@/components/onboarding/PlansScreen').then((m) => ({ default: m.PlansScreen }))
);

function WelcomeFlowContent() {
    if (!getWelcome()?.knowledgeOperation) return <Outlet />;

    return (
        <WorkspaceProvider>
            <KnowledgeOperationProgressProvider>
                <OnboardingOperationTracker />
                <Outlet />
            </KnowledgeOperationProgressProvider>
        </WorkspaceProvider>
    );
}

function LegacyConfigStudioRedirect({ from, to }: { from: string; to: string }) {
    const { pathname, search, hash } = useLocation();
    return <Navigate to={`${pathname.replace(from, to)}${search}${hash}`} replace />;
}

function AppContent() {
    return (
        <WorkspaceProvider>
            <WebsiteCrawlProgressProvider>
                <RetrainProgressProvider>
                    <EnvironmentProvider>
                        <PinnedPagesProvider>
                            <ReportingErrorBoundary boundaryName="app_root">
                                <AppLayout>
                                    <Suspense fallback={<div />}>
                                        <Routes>
                                            <Route path="/" element={<HomePage />} />
                                            <Route
                                                path="/config-studio/health"
                                                element={<ClientHealthPage />}
                                            />
                                            {/* Rose for Agents (IX-5245); clients see how to get it enabled (IX-5910) */}
                                            <Route
                                                path="/config-studio/rose-for-agents"
                                                element={<RoseForAgentsPage />}
                                            />
                                            <Route
                                                path="/config-studio/rose-for-agents/access"
                                                element={<RoseForAgentsAccessPage />}
                                            />
                                            <Route
                                                path="/config-studio/rose-for-agents/checks"
                                                element={<RoseForAgentsChecksPage />}
                                            />
                                            <Route
                                                path="/config-studio/rose-for-agents/profile"
                                                element={<RoseForAgentsProfilePage />}
                                            />
                                            <Route
                                                path="/analytics/topics"
                                                element={<TopTopicsPage />}
                                            />
                                            <Route
                                                path="/analytics/insights"
                                                element={<InsightsPage />}
                                            />
                                            <Route
                                                path="/analytics"
                                                element={
                                                    <Navigate
                                                        to="/analytics/conversations"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/analytics/:tab"
                                                element={<AnalyticsPage />}
                                            />
                                            <Route path="/accounts" element={<AccountsPage />} />
                                            <Route path="/visitors" element={<VisitorsPage />} />
                                            <Route
                                                path="/conversations"
                                                element={<ConversationsPage />}
                                            />
                                            <Route
                                                path="/topics"
                                                element={
                                                    <Navigate to="/analytics/topics" replace />
                                                }
                                            />

                                            {/* Config Studio opens on Website Agent config. */}
                                            <Route
                                                path="/config-studio"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/website-agent"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/website-agent/playground"
                                                element={<PlaygroundPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/evaluations"
                                                element={<PlaygroundDatasetsPage />}
                                            />

                                            {/* Shared Config Studio surfaces. Data Sources and
                                                Sales Guidelines are tabbed screens; the base path
                                                and the :tab param both render the section shell,
                                                which redirects an unknown/hidden tab to the first
                                                visible one and gates Website/Documents/Lookup per
                                                tab. */}
                                            <Route
                                                path="/config-studio/data-sources"
                                                element={<KnowledgeSection />}
                                            />
                                            <Route
                                                path="/config-studio/data-sources/:tab"
                                                element={<KnowledgeSection />}
                                            />
                                            <Route
                                                path="/config-studio/sales-guidelines"
                                                element={<GuidelinesSection />}
                                            />
                                            <Route
                                                path="/config-studio/sales-guidelines/:tab"
                                                element={<GuidelinesSection />}
                                            />

                                            {/* Website Agent — feature dashboard + detail pages */}
                                            <Route
                                                path="/config-studio/website-agent"
                                                element={<WebsiteAgentPage />}
                                            />
                                            {/* Publish — put Rose on the site (Day-1 install view) */}
                                            <Route
                                                path="/config-studio/website-agent/publish"
                                                element={<PublishPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/ai-sections"
                                                element={<AISectionsPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/curated-content"
                                                element={<CuratedContentPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/ab-test"
                                                element={<ClientExperimentPage />}
                                            />
                                            {/* ChatGPT Agent merged into Rose for Agents (IX-5910) */}
                                            <Route
                                                path="/config-studio/llm-agent"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/rose-for-agents"
                                                        replace
                                                    />
                                                }
                                            />
                                            {/* Content/GEO Agent — lands on the FAQ editor; settings behind its gear (IX-5775) */}
                                            <Route
                                                path="/config-studio/content-agent"
                                                element={<GeoFaqsPage />}
                                            />
                                            <Route
                                                path="/config-studio/content-agent/setup"
                                                element={<ContentAgentPage />}
                                            />
                                            <Route
                                                path="/config-studio/content-agent/observatory"
                                                element={<GeoQuestionsPage />}
                                            />
                                            <Route
                                                path="/config-studio/content-agent/faqs"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/content-agent/faqs"
                                                        to="/config-studio/content-agent"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/content-agent/page-style"
                                                element={
                                                    // Page Style is the Style step of the FAQ editor (IX-5775).
                                                    <Navigate
                                                        to="/config-studio/content-agent?step=style"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/content-agent/domain"
                                                element={<GeoDomainPage />}
                                            />
                                            <Route
                                                path="/config-studio/content-agent/settings"
                                                element={<ContentAgentSettingsPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/geo-questions"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/content-agent/observatory"
                                                        replace
                                                    />
                                                }
                                            />
                                            {/* Enhanced onboarding-step config screens (IX-3950) —
                                            registered before the :slug catch-all */}
                                            <Route
                                                path="/config-studio/brand"
                                                element={<BrandTonePage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/engagement"
                                                element={<SuggestedQuestionsPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/ctas"
                                                element={<CtaPage />}
                                            />
                                            <Route
                                                path="/config-studio/website-agent/:slug"
                                                element={<WebsiteAgentSettingPage />}
                                            />

                                            {/* Generic agent placeholder for agents without a dedicated dashboard */}
                                            <Route
                                                path="/config-studio/:agentId"
                                                element={<AgentPlaceholderPage />}
                                            />

                                            {/* Redirects from old URLs */}
                                            <Route
                                                path="/ai-sections"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/website-agent/ai-sections"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/curated-content"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/website-agent/curated-content"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/agents"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/website-agent"
                                                        replace
                                                    />
                                                }
                                            />

                                            <Route
                                                path="/dynamic-questions"
                                                element={<DynamicQuestionsPage />}
                                            />
                                            <Route
                                                path="/documentation"
                                                element={<DocumentationPage />}
                                            />
                                            <Route
                                                path="/config-studio/integration"
                                                element={<IntegrationsPage />}
                                            />
                                            {/* Legacy Config Studio URLs keep bookmarks working. */}
                                            <Route
                                                path="/config-studio/knowledge/website-sync"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/knowledge/website-sync"
                                                        to="/config-studio/data-sources/website"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/knowledge/pricing"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/knowledge/pricing"
                                                        to="/config-studio/sales-guidelines/pricing"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/knowledge/competitors"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/knowledge/competitors"
                                                        to="/config-studio/sales-guidelines/competitors"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/knowledge"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/knowledge"
                                                        to="/config-studio/data-sources"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/knowledge/*"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/knowledge"
                                                        to="/config-studio/data-sources"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/guidelines"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/guidelines"
                                                        to="/config-studio/sales-guidelines"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/guidelines/*"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/guidelines"
                                                        to="/config-studio/sales-guidelines"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/playground"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/playground"
                                                        to="/config-studio/website-agent/playground"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/config-studio/playground/evaluations"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/playground/evaluations"
                                                        to="/config-studio/website-agent/evaluations"
                                                    />
                                                }
                                            />
                                            {/* Report links shared before the rename carry ?item=. */}
                                            <Route
                                                path="/config-studio/playground/datasets"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/config-studio/playground/datasets"
                                                        to="/config-studio/website-agent/evaluations"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/content-agent"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/content-agent"
                                                        to="/config-studio/content-agent"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/content-agent/*"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/content-agent"
                                                        to="/config-studio/content-agent"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/chatgpt-agent"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/chatgpt-agent"
                                                        to="/config-studio/rose-for-agents"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/agents/:agentId"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/agents"
                                                        to="/config-studio"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/health"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/health"
                                                        to="/config-studio/health"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/knowledge/*"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/knowledge"
                                                        to="/config-studio/data-sources"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/integrations"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/integration"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/website-agent/geo-questions"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/content-agent/observatory"
                                                        replace
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/website-agent/brand"
                                                element={
                                                    <Navigate to="/config-studio/brand" replace />
                                                }
                                            />
                                            <Route
                                                path="/website-agent/*"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/website-agent"
                                                        to="/config-studio/website-agent"
                                                    />
                                                }
                                            />
                                            <Route path="/support" element={<SupportPage />} />
                                            <Route
                                                path="/playground"
                                                element={
                                                    <Navigate
                                                        to="/config-studio/website-agent/playground"
                                                        replace
                                                    />
                                                }
                                            />
                                            {/* Post-signup landing: once authenticated + provisioned,
                                            /signup has nothing left to show. */}
                                            <Route
                                                path="/signup"
                                                element={<Navigate to="/" replace />}
                                            />
                                            {/* Literal route registered before the
                                            /settings/:section? catch-all so it wins. */}
                                            <Route
                                                path="/settings/plan-and-usage"
                                                element={<PlanUsagePage />}
                                            />
                                            <Route
                                                path="/settings/compliance/:tab"
                                                element={<ComplianceSettingsPage />}
                                            />
                                            <Route
                                                path="/settings/compliance"
                                                element={<ComplianceSettingsPage />}
                                            />
                                            <Route
                                                path="/settings/:section?"
                                                element={<SettingsPage />}
                                            />
                                            <Route
                                                path="/design-system"
                                                element={<DesignSystemPage />}
                                            />
                                            {/* Super Admin landing + breadcrumb parent — sends
                                                the section root to its first page. */}
                                            <Route
                                                path="/superadmin"
                                                element={
                                                    <LegacyConfigStudioRedirect
                                                        from="/superadmin"
                                                        to="/superadmin/clients/stats"
                                                    />
                                                }
                                            />
                                            <Route
                                                path="/superadmin/clients/stats"
                                                element={<ClientsPage />}
                                            />
                                            <Route
                                                path="/superadmin/clients/edit"
                                                element={<ClientEditorPage />}
                                            />
                                            <Route
                                                path="/superadmin/clients/edit/:clientId"
                                                element={<ClientEditorPage />}
                                            />
                                            <Route
                                                path="/superadmin/users"
                                                element={<UsersPage />}
                                            />
                                            <Route
                                                path="/superadmin/sites"
                                                element={<AdminSitesPage />}
                                            />

                                            {/* Staff A/B experiments (IX-2958, IX-3024) */}
                                            <Route
                                                path="/superadmin/experiments"
                                                element={<StaffExperimentsPage />}
                                            />
                                            <Route
                                                path="/superadmin/experiments/new"
                                                element={<StaffExperimentNewPage />}
                                            />
                                            <Route
                                                path="/superadmin/experiments/:experimentId"
                                                element={<StaffExperimentDetailPage />}
                                            />

                                            {/* Staff conversion annotation queue (IX-3524) */}
                                            <Route
                                                path="/superadmin/annotations"
                                                element={<StaffAnnotationQueuePage />}
                                            />
                                            <Route
                                                path="/superadmin/person-identity"
                                                element={<PersonIdentityDiagnosticsPage />}
                                            />

                                            {/* Staff notice banner management (IX-4223) */}
                                            <Route
                                                path="/superadmin/notices"
                                                element={<StaffNoticesPage />}
                                            />
                                            <Route
                                                path="/superadmin/webmcp"
                                                element={<WebmcpUsagePage />}
                                            />
                                        </Routes>
                                    </Suspense>
                                </AppLayout>
                            </ReportingErrorBoundary>
                        </PinnedPagesProvider>
                    </EnvironmentProvider>
                </RetrainProgressProvider>
            </WebsiteCrawlProgressProvider>
        </WorkspaceProvider>
    );
}

export function App() {
    return (
        // Above every route, in-layout or not, so each one has a tab title.
        <PageChromeProvider>
            {/* Dialogs and sheets cover the header, so they carry their own
                feedback trigger (IX-4660). */}
            <ModalFeedbackProvider trigger={VisualFeedbackWidget}>
                <DocumentTitle />
                <Suspense fallback={<div />}>
                    <Routes>
                        <Route path="/auth/callback" element={<AuthCallbackPage />} />
                        {/* An AI assistant asks to connect through the Rose MCP (IX-5466).
                            Case-sensitive: public/_headers forbids framing this exact path. */}
                        <Route caseSensitive path="/oauth/consent" element={<OAuthConsentPage />} />
                        {/* An auth.md agent's person types its code here (IX-5476). Case-sensitive:
                            public/_headers forbids framing this path. */}
                        <Route caseSensitive path="/agent-claim" element={<AgentClaimPage />} />
                        <Route
                            path="/superadmin/dq-lab"
                            element={
                                <AuthGuard>
                                    <DynamicQuestionsLabPage />
                                </AuthGuard>
                            }
                        />
                        {/* Post-wizard welcome flow (IX-3929) — full-screen, outside AppLayout.
                    Reached only right after a domain claim; gated by a sessionStorage
                    handoff, which redirects home if visited without it. */}
                        <Route
                            element={
                                <AuthGuard>
                                    <WelcomeFlowContent />
                                </AuthGuard>
                            }
                        >
                            <Route path="/welcome/loader" element={<WelcomeLoader />} />
                            <Route path="/welcome/plans" element={<PlansScreen />} />
                        </Route>
                        <Route
                            path="/*"
                            element={
                                <AuthGuard>
                                    <AppContent />
                                </AuthGuard>
                            }
                        />
                    </Routes>
                </Suspense>
            </ModalFeedbackProvider>
        </PageChromeProvider>
    );
}
